import { useTestFormContext } from '@/app/features/reports/components/test-form/context'
import { FastFillIcon } from '@/components/raytd/icons'
import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ScrollArea } from '@/components/ui/scroll-area'
import TextareaAutosize from 'react-textarea-autosize'
import { PlusIcon } from 'lucide-react'
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { Control, useController, useFormContext } from 'react-hook-form'
import { cn } from "@/lib/utils"

// Helper function from HighlightableTextarea to escape special characters for RegExp
function escapeRegExp(string: string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

interface FastFillItem {
  id: number | string;
  description: string
}

interface FastFillTextAreaProps {
  name: string;
  control: Control<any>;
  placeholder?: string;
  fastFills: FastFillItem[];
  enableFastFill?: boolean;
  enableHighlighting?: boolean; // New prop to toggle highlighting
  disabled?: boolean;
  onCreateOption?: (value: string) => void;
  allowAdd?: boolean;
  className?: string;
  maxLength?: number;
}

export function FastFillText({
  name,
  control,
  placeholder,
  fastFills,
  enableFastFill = false,
  enableHighlighting: enableHighlightingProp = false, // Default highlighting to false
  disabled,
  className,
  onCreateOption,
  allowAdd = false,
  maxLength,
}: FastFillTextAreaProps) {

  const {
    field: { onChange, onBlur, value, ref },
    fieldState: { error }
  } = useController({
    name,
    control,
  });

  const methods = useFormContext();
  const { onFastFillChanged } = useTestFormContext();

  const [open, setOpen] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [inputValue, setInputValue] = useState('');
  const [validationTrigger, setValidationTrigger] = useState(0);
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  const deferredValidationTrigger = useDeferredValue(validationTrigger);

  // --- Start: Highlighting Logic ---
  const highlighterRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (highlighterRef.current && textareaRef.current) {
      highlighterRef.current.scrollTop = textareaRef.current.scrollTop;
      highlighterRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, []);

  const enableHighlighting = enableHighlightingProp && (fastFills?.length > 0 || allowAdd);

  const validSentences = useMemo(() => {
      if (!enableHighlighting) return [];
      return fastFills?.map(item => item.description) || [];
  }, [fastFills, enableHighlighting]);
  
  const highlightedText = useMemo(() => {
    if (!enableHighlighting || !value) return <span>{value}</span>;

    const matches: { start: number; end: number }[] = [];
    validSentences.forEach(sentence => {
      if (!sentence) return;
      const regex = new RegExp(escapeRegExp(sentence), 'g');
      let match;
      while ((match = regex.exec(value)) !== null) {
        matches.push({ start: match.index, end: match.index + sentence.length });
      }
    });

    if (matches.length === 0) {
      return <span className="bg-purple-100 dark:bg-red-900/40 rounded-sm">{value}</span>;
    }

    matches.sort((a, b) => a.start - b.start);
    const mergedMatches = matches.length > 0 ? [matches[0]] : [];
    for (let i = 1; i < matches.length; i++) {
      const current = matches[i];
      const last = mergedMatches[mergedMatches.length - 1];
      if (current.start <= last.end) {
        last.end = Math.max(last.end, current.end);
      } else {
        mergedMatches.push(current);
      }
    }

    const segments: { type: 'valid' | 'deviation'; content: string }[] = [];
    let lastIndex = 0;
    mergedMatches.forEach(({ start, end }) => {
      if (start > lastIndex) {
        segments.push({ type: 'deviation', content: value.substring(lastIndex, start) });
      }
      segments.push({ type: 'valid', content: value.substring(start, end) });
      lastIndex = end;
    });

    if (lastIndex < value.length) {
      segments.push({ type: 'deviation', content: value.substring(lastIndex, value.length) });
    }

    return segments.map((segment, index) => (
      <span
        key={index}
        className={
          segment.type === 'valid'
            ? 'bg-transparent dark:bg-transparent'
            : 'bg-purple-200 dark:bg-purple-900/40 rounded-sm'
        }
      >
        {segment.content}
      </span>
    ));
  }, [value, validSentences, enableHighlighting]);
  // --- End: Highlighting Logic ---

  const handleOnChange = useCallback((newValue: string) => {
    onChange(newValue);
    enableFastFill && onFastFillChanged(name);
    setValidationTrigger(prev => prev + 1);
  }, [onChange, name, onFastFillChanged, enableFastFill]);

  useEffect(() => {
    if (!open) { setInputValue('') }
  }, [open]);

  useEffect(() => {
    if (deferredValidationTrigger > 0) {
      methods.trigger(name);
    }
  }, [deferredValidationTrigger, methods.trigger, name]);

  const insertTextAtCursor = useCallback((text: string) => {
    if (!textareaRef.current) return;
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = textarea.value.substring(0, start) + text + textarea.value.substring(end);
    handleOnChange(newValue);
    const newCursorPosition = start + text.length;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        }
      });
    });
  }, [handleOnChange]);

  const handleFastFillSelect = useCallback((text: string) => {
    setOpen(false);
    insertTextAtCursor(text);
    methods.trigger(name);
    setTimeout(() => textareaRef.current?.focus(), 100);
  }, [insertTextAtCursor, methods.trigger, name]);

  const { viewportRef } = useTestFormContext();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  const handleOpenChange = useCallback((open: boolean) => {
    setOpen(open);
    if (!open) {
      setInputValue('');
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [setOpen, setInputValue]);

  const filteredFastFills = useMemo(() => {
    return fastFills?.filter((item) => item.description.toLowerCase().includes(inputValue.toLowerCase()));
  }, [fastFills, inputValue]);

  // Common classes for both the highlighter and the textarea for perfect alignment
  const commonTextAreaClasses = cn(
    "w-full px-3 py-2 text-sm resize-none",
    enableFastFill ? "pb-10" : "pb-2",
    className
  );
  
  return (
    <div 
      className={cn(
        "relative border border-input rounded-md transition-colors",
        isTextareaFocused && "border-ring ring-1 ring-ring/20",
        open && "z-50" 
      )} 
      ref={mainRef}
    >
      <div className={cn("relative w-full", enableHighlighting && "grid")}>
        {enableHighlighting && (
          <div
            ref={highlighterRef}
            className={cn(
              commonTextAreaClasses,
              'z-0 row-start-1 col-start-1 overflow-auto whitespace-pre-wrap break-words pointer-events-none',
              'min-h-[60px] border-none text-left' // Ensure consistent height and alignment
            )}
            aria-hidden="true"
          >
            {highlightedText}
            {/* Add a line break to prevent the last line from being clipped */}
            <br /> 
          </div>
        )}
        <TextareaAutosize
          ref={(e: HTMLTextAreaElement) => {
            ref(e)
            textareaRef.current = e
          }}
          id={name}
          value={value}
          onChange={(e) => handleOnChange(e.target.value)}
          onBlur={(e) => {
            onBlur()
            setIsTextareaFocused(false)
          }}
          onFocus={() => setIsTextareaFocused(true)}
          onScroll={enableHighlighting ? handleScroll : undefined} // Add scroll handler
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "flex bg-transparent placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 focus-visible:ring-0 focus-visible:ring-offset-0",
            "border-none rounded-md",
            commonTextAreaClasses, // Use common classes
            enableHighlighting && "z-10 row-start-1 col-start-1 text-transparent caret-primary" // Make text transparent
          )}
          spellCheck={true}
          minRows={3}
          cacheMeasurements
          maxLength={maxLength}
        />
      </div>

      {enableFastFill && (
        <div className="absolute bottom-0 left-0 right-0 border-t bg-muted/30 backdrop-blur-sm rounded-b-md z-20">
        <Popover
            open={open}
            onOpenChange={handleOpenChange}
            modal={true}
          >
            <PopoverTrigger asChild ref={triggerRef}>
              <Button
                variant="ghost"
                role="combobox"
                aria-expanded={open}
                size="sm"
                className="h-8 w-full justify-start text-xs font-normal text-muted-foreground hover:text-foreground rounded-none rounded-b-md border-none shadow-none"
                disabled={disabled}
              >
                <FastFillIcon className="mr-2 h-3 w-3 shrink-0" />
                Insert Fast Fill
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] p-0 h-[200px]"
              modal={true}
              sticky="always"
              collisionPadding={10}
              collisionBoundary={viewportRef?.current}
              side="top"
              align="start"
              onCloseAutoFocus={(e) => {
                e.preventDefault();
                textareaRef.current?.focus();
              }}
            >
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder={allowAdd ? 'Search or create new...' : "Search..."}
                  className="h-9"
                  value={inputValue}
                  onValueChange={setInputValue}
                />
                <ScrollArea className="h-full" type="auto">
                  <CommandGroup heading={inputValue ? `Search results` : "Fast Fills"}>
                    {filteredFastFills?.map((item) => (
                      <CommandItem
                        key={`fast-fill-${item.id}`}
                        onSelect={() => handleFastFillSelect(item.description)}
                        className="cursor-pointer"
                        value={item.description}
                      >
                        {item.description}
                      </CommandItem>
                    ))}
                    {filteredFastFills?.length === 0 && inputValue && (
                      <CommandItem value={`no-results-${inputValue}`} disabled>No results found.</CommandItem>
                    )}
                  </CommandGroup>
                  {allowAdd && inputValue && (
                    <>
                      <CommandSeparator alwaysRender />
                      <CommandGroup heading="Create New">
                        <CommandItem
                          key={`create-${inputValue}`}
                          value={inputValue}
                          onSelect={() => onCreateOption?.(inputValue)}>
                          <PlusIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                          Create "{inputValue}"
                        </CommandItem>
                      </CommandGroup>
                    </>
                  )}
                </ScrollArea>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  )
}

FastFillText.displayName = 'FastFillText';